import React, { useMemo, useRef, useState } from 'react';
import { Flex } from 'rebass';
import { useMutation } from '@apollo/client';
import {
  convertJobAttachment,
  convertJobComment,
  convertJobStatusChange,
} from './helpers';
import { sortHistoryItems } from '../Timeline/helpers';
import { gql } from '@/modes/individual/gql';
import Timeline from '../Timeline/Timeline';
import { GrowyTextarea } from '../Form/GrowyTextarea';
import { JobAttachment, JobComment, JobStatusChange } from './queries';
import { JobHistoryItem } from './types';
import RequirePerms from '../RequirePerms';

const CREATE_JOB_COMMENT = gql(`
  mutation CreateJobComment($input: JobCommentInput!) {
    createJobComment(input: $input) {
      id
    }
  }
`);

type JobHistoryProps = {
  jobId: string;
  comments: JobComment[];
  statusChanges: JobStatusChange[];
  attachments: JobAttachment[];
  refetch?: () => Promise<unknown>;
};

type JobHistoryType = 'COMMENT' | 'STATUS_CHANGE' | 'ATTACHMENT';

// History state
const jobHistoryItemTypes: JobHistoryType[] = [
  'COMMENT',
  'STATUS_CHANGE',
  'ATTACHMENT',
];

const JobHistory: React.FC<JobHistoryProps> = ({
  comments,
  statusChanges,
  attachments,
  jobId,
  refetch,
}) => {
  const [createJobComment] = useMutation(CREATE_JOB_COMMENT, {});
  const [comment, setComment] = useState('');

  const scrollDiv = useRef<any>();

  const matchVisibleHistoryTypes = (item: JobHistoryItem) => {
    // safety check, since not required
    if (!selectedHistoryItemTypes) {
      return true;
    }
    if (selectedHistoryItemTypes.includes('COMMENT')) {
      if (['COMMENT', 'APPROVAL'].includes(item.type)) {
        return true;
      }
    }
    if (selectedHistoryItemTypes.includes('STATUS_CHANGE')) {
      if (item.type === 'STATUS_CHANGE') {
        return true;
      }
    }
    if (selectedHistoryItemTypes.includes('ATTACHMENT')) {
      if (item.type === 'ATTACHMENT') {
        return true;
      }
    }
    return false;
  };

  const [selectedHistoryItemTypes, setSelectedHistoryItemTypes] =
    useState<JobHistoryType[]>(jobHistoryItemTypes);

  const amendedHistoryItems = useMemo<JobHistoryItem[]>(() => {
    const items: JobHistoryItem[] = [
      ...comments.map(convertJobComment),
      ...statusChanges.map(convertJobStatusChange),
      ...attachments.map(convertJobAttachment),
    ];

    // Convert, concat, filter, sort and group
    return items.filter(matchVisibleHistoryTypes).sort(sortHistoryItems);
  }, [
    comments,
    statusChanges,
    attachments,
    selectedHistoryItemTypes,
    matchVisibleHistoryTypes,
  ]);

  const availableHistoryItemTypes = useMemo(() => {
    const available: JobHistoryType[] = [];
    if (comments.length) {
      available.push('COMMENT');
    }
    if (attachments.length) {
      available.push('ATTACHMENT');
    }
    if (statusChanges.length) {
      available.push('STATUS_CHANGE');
    }
    return available;
  }, [comments, attachments, statusChanges]);

  return (
    <Flex flexDirection="column" height="100%" flexGrow={1} flexShrink={1}>
      <Timeline
        selectedHistoryItemTypes={selectedHistoryItemTypes}
        setSelectedHistoryItemTypes={setSelectedHistoryItemTypes}
        historyItemTypes={availableHistoryItemTypes}
        historyItems={amendedHistoryItems}
        scrollDivRef={scrollDiv}
      />

      {/************************************
       * Message Box
       ************************************/}
      <RequirePerms jobs="UPDATE">
        <Flex
          sx={{ borderTop: '1px solid', borderTopColor: 'tableBorder' }}
          flexGrow={0}
          flexShrink={0}
          p={4}
        >
          <GrowyTextarea
            height={34}
            focusedHeight={66}
            maxCharacters={1000}
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
            }}
            placeholder="Enter message"
            onKeyPress={async (e) => {
              if (e.key === 'Enter' || e.key === 'NumpadEnter') {
                e.preventDefault();
                if (comment.trim().length > 0) {
                  await createJobComment({
                    variables: {
                      input: {
                        jobId,
                        comment,
                      },
                    },
                  });
                  setComment('');
                  if (refetch) {
                    await refetch();
                  }
                  scrollDiv.current.scrollTop = 0;
                }
              }
            }}
          />
        </Flex>
      </RequirePerms>
    </Flex>
  );
};

export default JobHistory;
